import { supabase } from "@/integrations/supabase/client";

interface KnitApp {
  id: string;
  label: string;
  logo_short: string;
  logo_full: string;
  category: string;
}

// Cache constants
const CACHE_KEY = 'knit_ats_apps_cache';
const CACHE_EXPIRY = 15 * 24 * 60 * 60 * 1000; // 15 days in milliseconds

// Base URL for Knit helper function
const KNIT_HELPER_BASE_URL = 'https://jftsemrnjqbzinphyrpm.supabase.co/functions/v1/knit-helper';

/**
 * Fetches the list of available Knit apps with caching
 * @param forceRefresh Whether to force a refresh from the API regardless of cache
 * @returns Promise containing array of filtered Knit apps with ATS category
 */
export async function fetchKnitApps(forceRefresh = false): Promise<KnitApp[]> {
  try {
    // Check cache first if not forcing refresh
    if (!forceRefresh) {
      const cachedData = localStorage.getItem(CACHE_KEY);
      if (cachedData) {
        const { apps, timestamp } = JSON.parse(cachedData);
        const now = Date.now();
        // Use cache if it's less than expiry time old
        if (now - timestamp < CACHE_EXPIRY) {
          // console.log('Using cached ATS apps data');
          return apps;
        }
        // console.log('Cache expired, fetching fresh data');
      }
    }

    // If cache is not valid or forced refresh, fetch from database
    // console.log('Fetching ATS apps from database');
    
    // Get current user's session to include auth token
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('No authenticated session found');
    }
    
    // Fetch from the ats_integrations table
    const { data, error } = await supabase
      .from('ats_integrations')
      .select('*')
      .order('label');
    
    if (error) {
      throw new Error(`Error fetching ATS apps from database: ${error.message}`);
    }
    
    // Transform the data to match the KnitApp interface
    const atsApps = data.map(app => ({
      id: app.app_id,
      label: app.label,
      logo_short: app.logo_short,
      logo_full: app.logo_full,
      category: app.category
    }));
    
    // Save to cache with current timestamp
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      apps: atsApps,
      timestamp: Date.now()
    }));
    
    // console.log("Stored fresh ATS apps data in cache");
    return atsApps;
  } catch (error) {
    console.error('Error fetching Knit apps:', error);
    
    // Try to use cache even if expired in case of errors
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      if (cachedData) {
        const { apps } = JSON.parse(cachedData);
        // console.log('Using expired cache due to fetch error');
        return apps;
      }
    } catch (cacheError) {
      // console.error('Error accessing cache:', cacheError);
    }
    
    // If all else fails, try fetching from backup JSON file
    try {
      const atsApps = await fetchKnitAppsFromJson();
      return atsApps;
    } catch (jsonError) {
      console.error('Error fetching from JSON fallback:', jsonError);
    }
    
    return [];
  }
}

/**
 * Fetches Knit auth session token for a specific app
 * @param appId The ID of the app to create a session for
 * @returns Promise containing the auth session token
 */
export async function fetchKnitAuthSession(appId: string): Promise<string> {
  try {
    // console.log(`Starting to fetch Knit auth session for app: ${appId}`);
    
    // Get the current session to access the auth token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      // console.error("Session error:", sessionError);
      throw new Error(`Auth session error: ${sessionError.message}`);
    }
    
    if (!session) {
      throw new Error("No authenticated session found");
    }
    
    // console.log("Got authenticated session, proceeding to create Knit session");
    
    // Hit the backend to generate authSessionToken with proper authentication
    // console.log(`Sending request to ${KNIT_HELPER_BASE_URL}/create-session for app: ${appId}`);
    
    const response = await fetch(`${KNIT_HELPER_BASE_URL}/create-session`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ appId })
    });
    
    // console.log(`Response status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      // console.error(`Error response from server:`, errorText);
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(`Error fetching auth session: ${errorData.error || response.statusText}`);
      } catch (e) {
        throw new Error(`Error fetching auth session: ${response.statusText} - ${errorText.substring(0, 100)}`);
      }
    }
    
    const data = await response.json();
    // console.log("Response data received:", data.success ? "Success" : "Failed");
    
    if (!data.success || !data.token) {
      // console.error("Invalid response format:", data);
      throw new Error("Invalid response format: token not found");
    }
    
    return data.token;
  } catch (error) {
    // console.error("Error generating Knit auth session token:", error);
    throw error;
  }
}

/**
 * Deactivates an existing Knit integration
 * @param integrationId The ID of the integration to deactivate
 * @returns Promise containing the result of the deactivation
 */
export async function deactivateIntegration(integrationId: string): Promise<{ success: boolean; message: string }> {
  try {
    // Get the current session to access the auth token
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error("No authenticated session found");
    }
    
    const response = await fetch(`${KNIT_HELPER_BASE_URL}/deactivate-integration`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ integrationId })
    });
    
    if (!response.ok) {
      throw new Error(`Error deactivating integration: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || "Failed to deactivate integration");
    }
    
    return {
      success: true,
      message: "Integration deactivated successfully"
    };
  } catch (error) {
    // console.error("Error deactivating integration:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
}

/**
 * Force updates the ATS apps cache from the database
 */
export async function refreshAtsAppsCache(): Promise<void> {
  try {
    await fetchKnitApps(true);
    // console.log("ATS apps cache refreshed successfully");
  } catch (error) {
    // console.error("Failed to refresh ATS apps cache:", error);
  }
}

/**
 * Start a sync process for ATS jobs
 * @returns Promise containing the result of the sync operation
 */
export async function startAtsJobsSync(): Promise<{ success: boolean; message: string; data?: any }> {
  try {
    // Get the current session to access the auth token
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error("No authenticated session found");
    }
    
    // First, get the active integration ID for the current user's organization
    const { data: integrations, error: integrationError } = await supabase
      .from('knit_integrations')
      .select('*')
      .eq('active', true)
      .limit(1);
      
    if (integrationError) {
      throw new Error(`Error fetching integrations: ${integrationError.message}`);
    }
    
    if (!integrations || integrations.length === 0) {
      throw new Error("No active ATS integration found");
    }
    
    const integrationId = integrations[0].integration_id;
    
    // Call the backend to start the sync process
    const response = await fetch(`${KNIT_HELPER_BASE_URL}/start-sync`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ 
        integrationId,
        dataType: "ats_jobs",
        syncType: "delta_sync"
      })
    });
    
    if (!response.ok) {
      throw new Error(`Error starting sync: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || "Failed to start sync");
    }
    
    return {
      success: true,
      message: "ATS jobs sync started successfully",
      data: data.data
    };
  } catch (error) {
    // console.error("Error starting ATS jobs sync:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
}

/**
 * Start a sync process for ATS applicants
 * @returns Promise containing the result of the sync operation
 */
export async function startAtsApplicantsSync(): Promise<{ success: boolean; message: string; data?: any }> {
  try {
    // Get the current session to access the auth token
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error("No authenticated session found");
    }
    
    // First, get the active integration ID for the current user's organization
    const { data: integrations, error: integrationError } = await supabase
      .from('knit_integrations')
      .select('*')
      .eq('active', true)
      .limit(1);
      
    if (integrationError) {
      throw new Error(`Error fetching integrations: ${integrationError.message}`);
    }
    
    if (!integrations || integrations.length === 0) {
      throw new Error("No active ATS integration found");
    }
    
    const integrationId = integrations[0].integration_id;
    
    // Call the backend to start the sync process
    const response = await fetch(`${KNIT_HELPER_BASE_URL}/start-sync`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ 
        integrationId,
        dataType: "ats_applications",
        syncType: "delta_sync"
      })
    });
    
    if (!response.ok) {
      throw new Error(`Error starting sync: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || "Failed to start sync");
    }
    
    return {
      success: true,
      message: "ATS applicants sync started successfully",
      data: data.data
    };
  } catch (error) {
    // console.error("Error starting ATS applicants sync:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
}

/**
 * Fetches all ATS jobs from the database
 * @returns Promise containing array of ATS jobs
 */
export async function fetchAtsJobs() {
  try {
    // With RLS in place, this will automatically filter to jobs from the user's organization
    // due to the RLS policy we created
    const { data, error } = await supabase
      .from('ats_jobs')
      .select('*')
      .order('updated_at', { ascending: false });
      
    if (error) {
      throw error;
    }
    
    return data || [];
  } catch (error) {
    // console.error('Error fetching ATS jobs:', error);
    throw error;
  }
}

/**
 * Creates a note for a candidate in the ATS system
 * @param candidateId The ID of the candidate
 * @param applicationId The ID of the application
 * @param note The note content including assessment summary, score and shortened URL
 * @returns Promise containing the result of the note creation
 */
export async function createCandidateNote(
  candidateId: string,
  applicationId: string,
  note: string
): Promise<{ success: boolean; message: string; data?: any }> {
  try {
    // Get the current session to access the auth token
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error("No authenticated session found");
    }
    
    // Get the active integration ID for the current user's organization
    const { data: integrations, error: integrationError } = await supabase
      .from('knit_integrations')
      .select('*')
      .eq('active', true)
      .limit(1);
      
    if (integrationError) {
      throw new Error(`Error fetching integrations: ${integrationError.message}`);
    }
    
    if (!integrations || integrations.length === 0) {
      throw new Error("No active ATS integration found");
    }
    
    const integrationId = integrations[0].integration_id;
    
    // Call the backend to create the candidate note
    const response = await fetch(`${KNIT_HELPER_BASE_URL}/create-candidate-note`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ 
        integrationId,
        candidateId,
        applicationId,
        note
      })
    });
    
    if (!response.ok) {
      throw new Error(`Error creating candidate note: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || "Failed to create candidate note");
    }
    
    return {
      success: true,
      message: "Candidate note created successfully",
      data: data.data
    };
  } catch (error) {
    // console.error("Error creating candidate note:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
}

/**
 * Push candidate assessment results to ATS
 * @param candidateId The ID of the candidate in the RoundOne system
 * @param customNote Optional custom note to send instead of the auto-generated one
 */
export async function pushToATS(candidateId: string, customNote?: string) {
  try {
    const { data: authData } = await supabase.auth.getSession();
    if (!authData.session) {
      throw new Error('No active session');
    }

    const response = await fetch(`${KNIT_HELPER_BASE_URL}/push-to-ats`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authData.session.access_token}`
      },
      body: JSON.stringify({
        candidateId,
        note: customNote
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to push to ATS');
    }

    return await response.json();
  } catch (error) {
    // console.error('Error pushing to ATS:', error);
    throw error;
  }
}

/**
 * Fetches the list of available Knit apps from the public atslist.json file
 * @returns Promise containing array of Knit apps with ATS category
 */
export async function fetchKnitAppsFromJson(): Promise<KnitApp[]> {
  try {
    const response = await fetch('/ats-logos/atslist.json');
    const data = await response.json();
    
    if (!data.success || !data.data) {
      throw new Error('Invalid atslist.json format');
    }
    
    // Filter only ATS apps and transform to match KnitApp interface
    const atsApps = data.data
      .filter((app: any) => app.category === 'ATS')
      .map((app: any) => ({
        id: app.id,
        label: app.label,
        logo_short: app.logo_short,
        logo_full: app.logo_full,
        category: app.category
      }));
    
    return atsApps;
  } catch (error) {
    console.error('Error fetching ATS apps from atslist.json:', error);
    return [];
  }
}

export default {
  fetchKnitApps,
  fetchKnitAuthSession,
  refreshAtsAppsCache,
  deactivateIntegration,
  startAtsJobsSync,
  startAtsApplicantsSync,
  fetchAtsJobs,
  createCandidateNote,
  pushToATS,
  fetchKnitAppsFromJson
}; 